import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { TabPanel } from 'react-tabs';
import { css } from '@emotion/css';
import { useHistory, useLocation } from 'react-router-dom';
import OrganizationContainer from '../../../../../shared/state/containers/OrganizationContainer';
import MediaContainer from '../../../../../shared/state/containers/MediaContainer';
import UserContainer from '../../../../../shared/state/containers/UserContainer';
import MemberContainer from '../../../../../shared/state/containers/MemberContainer';
import TabContainerPanel from '../../../../../shared/components/structure/Panels/TabContainerPanel';
import ScrollablePanelBody from '../../../../../shared/components/structure/ScrollablePanelBody';
import OrganizationInfoSettings from '../../../../../shared/scenes/organizationSettings/components/OrganizationInfoSettings';
import OrganizationIntegrationsSettings from '../../../../../shared/scenes/organizationSettings/components/OrganizationIntegrationsSettings';
import OrganizationApplications from './OrganizationApplications';
import OnboardingLinksCard from './OnboardingLinksCard';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import TemplateFormSectionModel from '../../../../../shared/models/templating/api/TemplateFormSectionModel';
import TemplateFormFieldModel from '../../../../../shared/models/templating/api/TemplateFormFieldModel';
import ApplicationContainer from '../../../../../shared/state/containers/ApplicationContainer';
import TemplateConfigurationContainer from '../../../../../shared/state/containers/TemplateConfigurationContainer';
import ApiKeyContainer from '../../../../../shared/state/containers/ApiKeyContainer';
import ConfirmationDialog from '../../../../../shared/components/structure/ConfirmationDialog';
import styled from '@emotion/styled';
import { authenticateJobber, authenticateSalesforce, authenticateZohoCRM } from '../../../../../shared/api/controllers/businessManagement';
import ScheduledEmailForm from '../../../../../shared/components/forms/ScheduledEmailForm';
import OrganizationVariablesContainer from '../../../../../shared/hooks/OrganizationVariablesContainer';
import SparkOrganizationManagementSettings from './SparkOrganizationManagementSettings';
import AppliedApplicationTemplateContainer from '../../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../../../shared/state/containers/ApplicationEnvironmentsContainer';

const basicInfoBlue = require('../../../../../shared/content/images/organization-settings/basic-info-blue.svg');
const basicInfoDark = require('../../../../../shared/content/images/organization-settings/basic-info-dark.svg');
const locationsBlue = require("../../../../../shared/content/images/spark/configuration-blue.svg");
const locationsDark = require("../../../../../shared/content/images/spark/configuration-dark.svg");
const integrationsBlue = require('../../../../../shared/content/images/organization-settings/integrations-tab-blue.svg');
const integrationsDark = require('../../../../../shared/content/images/organization-settings/integrations-tab-dark.svg');
const sendEmailIcon = require('../../../../../shared/content/images/read-email-monitor.svg');
const onboardingBlue = require('../../../../../shared/content/images/ssml/hyperlink-3-blue.svg');
const onboardingDark = require('../../../../../shared/content/images/ssml/hyperlink-3-dark.svg');
const constantsBlue = require('../../../../../shared/content/images/tab-icons/constants-blue.svg');
const constantsDark = require('../../../../../shared/content/images/tab-icons/constants-dark.svg');

interface OrganizationSettingsProps {
    appContainer: ApplicationContainer;
    apiKeyContainer: ApiKeyContainer;
    orgContainer: OrganizationContainer;
    mediaContainer: MediaContainer;
    userContainer: UserContainer;
    memberContainer: MemberContainer;
    commonApp: ApplicationModel;
    locationApps?: ApplicationModel[];
    manageMenuApps?: ApplicationModel[];
    locationAppNames: string[];
    commonAppSections: TemplateFormSectionModel[];
    locationAppFormFields: TemplateFormFieldModel[];
    isLoadingTemplates: boolean;
    history: any;
    hideApplicationSettings?: boolean;
    padding?: string;
    templateConfigurationContainer: TemplateConfigurationContainer;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
}

const OrganizationSettingsTabs: React.FC<OrganizationSettingsProps> = (props) => {
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const history = useHistory();
    const location = useLocation();

    const indexFromPathname = (pathname: string): number => {
        const slugs = props.hideApplicationSettings
            ? ['orgInfo', 'onboarding', 'integrations', 'emails', 'management']
            : ['orgInfo', 'configuration', 'onboarding', 'integrations', 'emails', 'management'];
        
        const index = slugs.findIndex(slug => pathname.indexOf(slug) > -1);
        return index > -1 ? index : 0;
    };

    const [activeIndex, setActiveIndex] = useState(() => indexFromPathname(location.pathname));

    useEffect(() => {
        setActiveIndex(indexFromPathname(location.pathname));
    }, [location.pathname, props.hideApplicationSettings]);

    useEffect(() => {
        props.userContainer.getUser();
        props.memberContainer.checkIsOrgAdmin(props.orgContainer.state.currentOrganization?.id, props.userContainer.state.currentUser?.id);

        // Check for Jobber auth code
        handleIntegrationAuthenticationRedirect();

    }, []);

    const handleIntegrationAuthenticationRedirect = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);

            const error = urlParams.get('error');
            let errorDescription = "An error occurred during authentication";
            if (urlParams.get('error_description')) {
                errorDescription = urlParams.get('error_description');
            }

            if (error) {
                setErrorMessage(errorDescription + " - Please contact Voicify support if the issue persists.");
                setShowErrorModal(true);

                // Clean URL params
                history.replace(window.location.pathname);
                return;
            }

            const authorizationCode = urlParams.get('code');
            const state = urlParams.get('state');

            if (!authorizationCode || !state) {
                return;
            }
            const integrationType = state.split('-')[0];

            const applicationId = state.split('-').slice(1).join('-');;

            const handleJobberAuthentication = async (authorizationCode: string, applicationId: string) => {
                const result = await authenticateJobber({
                    applicationId: applicationId,
                    authorizationCode
                });
                if (result.resultType === "Ok" && result.data) {
                    setShowSuccessModal(true);
                    //remove the code and state from the url
                    history.replace(window.location.pathname);
                } else {
                    setErrorMessage('Failed to connect your Jobber account. Please contact Voicify support if the issue persists.');
                    setShowErrorModal(true);
                }
            }

            const handleSalesforceAuthentication = async (authorizationCode: string) => {
                //The authorization code ends with %3D%3D, we need to change that to ==
                authorizationCode = authorizationCode.replace("%3D%3D", "==");
                //state looks like Salesforce-${selectedLocation.salesforce}-id-${clientId}-secret-${salesforceCredentials.clientSecret}
                const stateString = state;

                const applicationId = stateString.substring(
                    stateString.indexOf('Salesforce-') + 'Salesforce-'.length,
                    stateString.indexOf('-id-')
                );

                const clientId = stateString.substring(
                    stateString.indexOf('-id-') + '-id-'.length,
                    stateString.indexOf('-secret-')
                );
                const clientSecret = stateString.substring(
                    stateString.indexOf('-secret-') + '-secret-'.length
                );

                const result = await authenticateSalesforce({
                    applicationId: applicationId,
                    authorizationCode,
                    clientId,
                    clientSecret
                });
                if (result.resultType === "Ok" && result.data) {
                    setShowSuccessModal(true);
                    //remove the code and state from the url
                    history.replace(window.location.pathname);
                } else {
                    setErrorMessage('Failed to connect your Salesforce account. Please contact Voicify support if the issue persists.');
                    setShowErrorModal(true);
                }
            }

            const handleZohoCRMAuthentication = async (authorizationCode: string) => {
                // State format: ZohoCRM-${selectedLocation}-id-${clientId}-secret-${clientSecret}

                const stateString = state;
                const applicationId = stateString.substring(
                    stateString.indexOf('ZohoCRM-') + 'ZohoCRM-'.length,
                    stateString.indexOf('-id-')
                );

                const clientId = stateString.substring(
                    stateString.indexOf('-id-') + '-id-'.length,
                    stateString.indexOf('-secret-')
                );
                const clientSecret = stateString.substring(
                    stateString.indexOf('-secret-') + '-secret-'.length
                );

                const accountsServerString = urlParams.get('accounts-server');

                //convert the accounts server with %3A to :
                const accountServer = decodeURIComponent(accountsServerString);

                const result = await authenticateZohoCRM({
                    applicationId: applicationId, // Use the parsed ID
                    authorizationCode,
                    accountServer,
                    clientId,      // Pass clientId
                    clientSecret   // Pass clientSecret
                });
                if (result.resultType === "Ok" && result.data) {
                    setShowSuccessModal(true);
                    //remove the code and state from the url
                    history.replace(window.location.pathname);
                } else {
                    setErrorMessage('Failed to connect your Zoho CRM account. Please contact Voicify support if the issue persists.');
                    setShowErrorModal(true);
                }
            }

            switch (integrationType) {
                case "Jobber":
                    handleJobberAuthentication(authorizationCode, applicationId);
                    break;
                case "Salesforce":
                    handleSalesforceAuthentication(authorizationCode);
                    break;
                case "ZohoCRM":
                    handleZohoCRMAuthentication(authorizationCode);
                    break;
            }

        } catch (error) {
            setErrorMessage('An unexpected error occurred during authentication. Please try again or contact Voicify support if the issue persists.');
            setShowErrorModal(true);
        }
    };

    const handleTabSelection = (index: number, last: number) => {
        if (index === last) return;
        setActiveIndex(index);
        const urlStart = '/v/orgSettings/';
        const slugs = props.hideApplicationSettings
            ? ['orgInfo', 'onboarding', 'integrations', 'emails', 'management']
            : ['orgInfo', 'configuration', 'onboarding', 'integrations', 'emails', 'management'];
        history.push(index === 0 ? '/v/orgSettings' : urlStart + slugs[index]);
    };

    const renderTabs = () => {
        if (props.hideApplicationSettings) {
            return [
                {
                    title: 'Org Info',
                    icon: basicInfoDark,
                    selectedIcon: basicInfoBlue
                },
                {
                    title: 'Onboarding',
                    icon: onboardingDark,
                    selectedIcon: onboardingBlue
                },
                {
                    title: 'Integrations',
                    icon: integrationsDark,
                    selectedIcon: integrationsBlue
                },
                {
                    title: 'Emails',
                    icon: sendEmailIcon,
                    selectedIcon: sendEmailIcon
                },
                {
                    title: 'Management',
                    icon: constantsDark,
                    selectedIcon: constantsBlue
                }
            ];
        }

        return [
            {
                title: 'Org Info',
                icon: basicInfoDark,
                selectedIcon: basicInfoBlue
            },
            {
                title: 'Configuration',
                icon: locationsDark,
                selectedIcon: locationsBlue
            },
            {
                title: 'Onboarding',
                icon: onboardingDark,
                selectedIcon: onboardingBlue
            },
            {
                title: 'Integrations',
                icon: integrationsDark,
                selectedIcon: integrationsBlue
            },
            {
                title: 'Emails',
                icon: sendEmailIcon,
                selectedIcon: sendEmailIcon
            },
            {
                title: 'Management',
                icon: constantsDark,
                selectedIcon: constantsBlue
            }
        ];
    };

    return (
        <>
            <TabContainerPanel
                selectedIndex={activeIndex}
                onTabSelected={handleTabSelection}
                tabs={renderTabs()}
            >
                {props.hideApplicationSettings ? (
                    <>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 168px)"}>
                                <OrganizationInfoSettings
                                    orgContainer={props.orgContainer}
                                    mediaContainer={props.mediaContainer}
                                    templateConfigurationContainer={props.templateConfigurationContainer}
                                    commonApp={props.commonApp}
                                    appContainer={props.appContainer}
                                />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 168px)"}>
                                <OnboardingLinksCard
                                    locationApps={props.locationApps}
                                    memberContainer={props.memberContainer}
                                    userContainer={props.userContainer}
                                    apiKeyContainer={props.apiKeyContainer}
                                    organizationId={props.orgContainer.state.currentOrganization?.id}
                                    isLoading={props.isLoadingTemplates}
                                />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 168px)"}>
                                <OrganizationIntegrationsSettings organizationId={props.orgContainer.state.currentOrganization?.id} />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 168px)"}>
                                <ScheduledEmailForm organizationId={props.orgContainer.state.currentOrganization?.id} orgName={props.orgContainer.state.currentOrganization.name} appContainer={props.appContainer} />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 168px)"}>
                                <OrganizationVariablesContainer.Provider>
                                    <SparkOrganizationManagementSettings
                                        organizationId={props.orgContainer.state.currentOrganization?.id}
                                        locationApps={props.locationApps}
                                        commonApp={props.commonApp}
                                        appliedTemplateContainer={props.appliedTemplateContainer}
                                        applicationEnvironmentsContainer={props.applicationEnvironmentsContainer}
                                        appContainer={props.appContainer}
                                    />
                                </OrganizationVariablesContainer.Provider>
                            </ScrollablePanelBody>
                        </TabPanel>
                    </>
                ) : (
                    <>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                                <OrganizationInfoSettings
                                    orgContainer={props.orgContainer}
                                    mediaContainer={props.mediaContainer}
                                    templateConfigurationContainer={props.templateConfigurationContainer}
                                    commonApp={props.commonApp}
                                    appContainer={props.appContainer}
                                />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                                <OrganizationApplications
                                    appContainer={props.appContainer}
                                    orgContainer={props.orgContainer}
                                    commonApp={props.commonApp}
                                    locationApps={props.locationApps}
                                    manageMenuApps={props.manageMenuApps}
                                    locationAppNames={props.locationAppNames}
                                    commonAppSections={props.commonAppSections}
                                    locationAppFormFields={props.locationAppFormFields}
                                    isLoadingTemplates={props.isLoadingTemplates}
                                    history={props.history}
                                />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                                <OnboardingLinksCard
                                    locationApps={props.locationApps}
                                    memberContainer={props.memberContainer}
                                    userContainer={props.userContainer}
                                    apiKeyContainer={props.apiKeyContainer}
                                    organizationId={props.orgContainer.state.currentOrganization?.id}
                                    isLoading={props.isLoadingTemplates}
                                />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                                <OrganizationIntegrationsSettings organizationId={props.orgContainer.state.currentOrganization?.id} />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                                <ScheduledEmailForm organizationId={props.orgContainer.state.currentOrganization?.id} orgName={props.orgContainer.state.currentOrganization.name} appContainer={props.appContainer} isSparkView={true} />
                            </ScrollablePanelBody>
                        </TabPanel>
                        <TabPanel>
                            <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                                <OrganizationVariablesContainer.Provider>
                                    <SparkOrganizationManagementSettings
                                        organizationId={props.orgContainer.state.currentOrganization?.id}
                                        locationApps={props.locationApps}
                                        commonApp={props.commonApp}
                                        appliedTemplateContainer={props.appliedTemplateContainer}
                                        applicationEnvironmentsContainer={props.applicationEnvironmentsContainer}
                                        appContainer={props.appContainer}
                                    />
                                </OrganizationVariablesContainer.Provider>
                            </ScrollablePanelBody>
                        </TabPanel>
                    </>
                )}
            </TabContainerPanel>

            {showSuccessModal && (
                <ConfirmationDialog
                    title="Account Connected Successfully"
                    onClose={() => setShowSuccessModal(false)}
                    onConfirm={() => setShowSuccessModal(false)}
                    deleteText="OK"
                >
                    <ConfirmationDialogText>
                        <p>Your account has been successfully connected. Your assistant can now access client and customer information.</p>
                    </ConfirmationDialogText>
                </ConfirmationDialog>
            )}

            {showErrorModal && (
                <ConfirmationDialog
                    title="Account Connection Failed"
                    onClose={() => {
                        setShowErrorModal(false);
                        setErrorMessage('');
                    }}
                    onConfirm={() => {
                        setShowErrorModal(false);
                        setErrorMessage('');
                    }}
                    deleteText="OK"
                >
                    <ConfirmationDialogText>
                        <p>{errorMessage}</p>
                    </ConfirmationDialogText>
                </ConfirmationDialog>
            )}
        </>
    );
};

const paddedBody = css`
    padding: 32px 32px 0 32px;
`;

const ConfirmationDialogText = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 24px;
    height: 90%; //because of the footer
    width: 100%;
`;
export default OrganizationSettingsTabs;
